/**
 * 日焼け止め防御モデル（§4.5）。
 *   P(t) = 1 + (SPF_label^r − 1) × d(t)   … P=1 が未塗布相当
 *   d(t) = exp(−(t − t_app)/τ) × Π swim_w  … 時間減衰＋洗い流し
 * フェイルセーフ：塗布記録がない区間は P=1（未塗布）で積分する。
 */
import {
  APPLY_AMOUNT_R,
  HOT_TEMP_C,
  HUMID_RH,
  SWIM_KEEP_NORMAL,
  SWIM_KEEP_WATERPROOF,
  TAU_HOT_HUMID_S,
  TAU_NORMAL_S,
} from "./constants.js";
import type { Application, Ms, WxMinute } from "./types.js";

/** 環境に応じた減衰時定数 [s]。高温多湿で半減（§4.5） */
export function tauSeconds(wx: Pick<WxMinute, "tempC" | "rh">): number {
  return wx.tempC >= HOT_TEMP_C && wx.rh >= HUMID_RH ? TAU_HOT_HUMID_S : TAU_NORMAL_S;
}

/** 残存率 d(t) ∈ (0,1]。swimFactor は水泳申告で更新済みの累積係数 */
export function decay(app: Application, now: Ms, wx: Pick<WxMinute, "tempC" | "rh">): number {
  const dt = Math.max(0, (now - app.ts) / 1000);
  return Math.exp(-dt / tauSeconds(wx)) * app.swimFactor;
}

/** 防御係数 P(t) ≥ 1。app=null（未記録）は P=1 のフェイルセーフ（§4.5・§9.3） */
export function protectionFactor(
  app: Application | null,
  now: Ms,
  wx: Pick<WxMinute, "tempC" | "rh">,
): number {
  if (!app || now < app.ts) return 1;
  const r = APPLY_AMOUNT_R[app.amount];
  const effectiveSpf = Math.pow(app.spfLabel, r);
  return 1 + (effectiveSpf - 1) * decay(app, now, wx);
}

/** 水泳申告：d ← d × w（§4.5）。Application を破壊せず更新後の値を返す */
export function applySwim(app: Application): Application {
  const w = app.waterproof ? SWIM_KEEP_WATERPROOF : SWIM_KEEP_NORMAL;
  return { ...app, swimFactor: app.swimFactor * w };
}
